import { memo, useEffect } from "react";
import { generatePath, Link, useParams } from "react-router";

import { sanitize } from "@/lib/dompurify";

import { PATHS } from "@/routes/paths";

import type { SearchedContent } from "@/components/features/faq/ui/hooks/search-contents/type";

type Props = {
	title: string;
	contents: SearchedContent;
};

export const Contents = memo(({ title, contents }: Props) => {
	const { hotelID } = useParams<{ hotelID: string }>();

	useEffect(() => {
		const scrollToHash = () => {
			const hash = window.location.hash;
			if (hash) {
				setTimeout(() => {
					// Remove # and use getElementById for safe DOM access
					const hashId = hash.replace(/^#/, "");
					const el = document.getElementById(hashId);
					if (el) {
						el.scrollIntoView({ behavior: "smooth" });
					}
				}, 100);
			}
		};
		scrollToHash();
	}, []);

	return (
		<div className="w-full">
			<h3 id={title} className="scroll-mt-20 text-xl font-bold">
				{title}向け
			</h3>

			<div className="ml-4 mt-2 grid gap-y-6">
				{contents.map((content) => (
					<div key={content.categoryKey}>
						<h4
							id={`${title}-${content.categoryKey}`}
							className="mb-2 scroll-mt-20 font-bold"
							dangerouslySetInnerHTML={{ __html: sanitize(content.categoryText) }}
						/>
						<div className="space-y-3 rounded-lg border border-zinc-100 bg-white px-8 py-6">
							{content.subcategories.map((subcategory, idx) => (
								<ol key={`${content.categoryKey}-${subcategory.key}-${idx}`}>
									<li>
										<p
											id={`${title}-${subcategory.key}`}
											className="text-base font-semibold"
											dangerouslySetInnerHTML={{ __html: sanitize(subcategory.text) }}
										/>
									</li>
									{subcategory.contents.map((item) => {
										const notionID = item.id.replace(/-/g, "");
										const detailPath = generatePath(PATHS.FAQ_DETAIL, {
											hotelID: hotelID ?? "",
											notionID: encodeURIComponent(notionID),
										});

										return (
											<li key={item.id} className="ml-2 mt-1">
												<Link to={detailPath} viewTransition>
													<span
														className="cursor-pointer text-sm hover:underline hover:opacity-70"
														dangerouslySetInnerHTML={{
															__html: sanitize(item.text),
														}}
													/>
												</Link>
											</li>
										);
									})}
								</ol>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
});
